import React, { useState, useEffect, useRef } from 'react';
import { ChevronLeft, ChevronRight, X } from 'lucide-react';
import Input from './Input';
import Select from './Select';

export interface DateTimePickerValue {
  datetime: Date; // Date object
  timestamp: number; // Unix timestamp in seconds
  timezone: string;
}

interface DateTimePickerProps {
  value?: DateTimePickerValue | null;
  onChange: (value: DateTimePickerValue) => void;
  onClose: () => void;
  isOpen: boolean;
}

// Get local timezone with offset
const getLocalTimezone = () => {
  const localTZ = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const now = new Date();
  const offset = -now.getTimezoneOffset(); // Offset in minutes
  const offsetHours = Math.floor(Math.abs(offset) / 60);
  const offsetMinutes = Math.abs(offset) % 60;
  const sign = offset >= 0 ? '+' : '-';
  const offsetStr = `${sign}${String(offsetHours).padStart(2, '0')}:${String(offsetMinutes).padStart(2, '0')}`;

  // Try to get a friendly name
  const formatter = new Intl.DateTimeFormat('en', {
    timeZone: localTZ,
    timeZoneName: 'long',
  });
  const parts = formatter.formatToParts(now);
  const tzName = parts.find((p) => p.type === 'timeZoneName')?.value || localTZ;

  return { value: localTZ, label: `(UTC${offsetStr}) Local - ${tzName}` };
};

// Common timezones list
const getTimezones = () => {
  const localTZ = getLocalTimezone();
  return [
    localTZ, // Local timezone first
    { value: 'UTC', label: '(UTC+00:00) UTC' },
    { value: 'America/New_York', label: '(UTC-05:00) Eastern Time' },
    { value: 'America/Chicago', label: '(UTC-06:00) Central Time' },
    { value: 'America/Denver', label: '(UTC-07:00) Mountain Time' },
    { value: 'America/Los_Angeles', label: '(UTC-08:00) Pacific Time' },
    { value: 'Europe/London', label: '(UTC+00:00) London' },
    { value: 'Europe/Paris', label: '(UTC+01:00) Paris' },
    { value: 'Europe/Berlin', label: '(UTC+01:00) Berlin' },
    { value: 'Asia/Dubai', label: '(UTC+04:00) Dubai' },
    {
      value: 'Asia/Kolkata',
      label: '(UTC+05:30) Chennai, Kolkata, Mumbai, New Delhi',
    },
    { value: 'Asia/Shanghai', label: '(UTC+08:00) Beijing, Shanghai' },
    { value: 'Asia/Tokyo', label: '(UTC+09:00) Tokyo' },
    { value: 'Australia/Sydney', label: '(UTC+10:00) Sydney' },
  ];
};

const DateTimePicker: React.FC<DateTimePickerProps> = ({
  value,
  onChange,
  onClose,
  isOpen,
}) => {
  // Helper function to get initial values from value prop
  const getInitialValues = () => {
    if (value?.datetime) {
      const utcDate = new Date(value.datetime);
      const tz =
        value.timezone ||
        Intl.DateTimeFormat().resolvedOptions().timeZone ||
        'UTC';

      // Convert UTC date to display in the selected timezone
      const formatter = new Intl.DateTimeFormat('en-US', {
        timeZone: tz,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false,
      });

      const parts = formatter.formatToParts(utcDate);
      const year = parseInt(
        parts.find((p) => p.type === 'year')?.value || '0',
        10
      );
      const month =
        parseInt(parts.find((p) => p.type === 'month')?.value || '0', 10) - 1;
      const day = parseInt(
        parts.find((p) => p.type === 'day')?.value || '0',
        10
      );
      const hour = parseInt(
        parts.find((p) => p.type === 'hour')?.value || '0',
        10
      );
      const minute = parseInt(
        parts.find((p) => p.type === 'minute')?.value || '0',
        10
      );
      const second = parseInt(
        parts.find((p) => p.type === 'second')?.value || '0',
        10
      );

      return {
        date: new Date(year, month, day),
        month,
        year,
        hours: hour % 12 || 12,
        minutes: minute,
        seconds: second,
        ampm: (hour >= 12 ? 'PM' : 'AM') as 'AM' | 'PM',
        timezone: tz,
        timestamp: value.timestamp || Math.floor(utcDate.getTime() / 1000),
      };
    }

    const now = new Date();
    return {
      date: now,
      month: now.getMonth(),
      year: now.getFullYear(),
      hours: 12,
      minutes: 0,
      seconds: 0,
      ampm: 'AM' as 'AM' | 'PM',
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC',
      timestamp: 0,
    };
  };

  const initialValues = getInitialValues();

  const [selectedDate, setSelectedDate] = useState<Date>(initialValues.date);
  const [displayMonth, setDisplayMonth] = useState(initialValues.month);
  const [displayYear, setDisplayYear] = useState(initialValues.year);
  const [hours, setHours] = useState(initialValues.hours);
  const [minutes, setMinutes] = useState(initialValues.minutes);
  const [seconds, setSeconds] = useState(initialValues.seconds);
  const [ampm, setAmpm] = useState<'AM' | 'PM'>(initialValues.ampm);
  const [timezone, setTimezone] = useState(initialValues.timezone);
  const [unixTimestamp, setUnixTimestamp] = useState(initialValues.timestamp);

  const modalRef = useRef<HTMLDivElement>(null);
  const isUpdatingFromTimestamp = useRef(false);

  // Update selected date when value changes
  useEffect(() => {
    if (value?.datetime) {
      isUpdatingFromTimestamp.current = true;

      const utcDate =
        value.datetime instanceof Date
          ? value.datetime
          : new Date(value.datetime);
      const tz =
        value.timezone ||
        Intl.DateTimeFormat().resolvedOptions().timeZone ||
        'UTC';

      // Convert UTC date to display in the selected timezone
      const formatter = new Intl.DateTimeFormat('en-US', {
        timeZone: tz,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false,
      });

      const parts = formatter.formatToParts(utcDate);
      const year = parseInt(
        parts.find((p) => p.type === 'year')?.value || '0',
        10
      );
      const month =
        parseInt(parts.find((p) => p.type === 'month')?.value || '0', 10) - 1;
      const day = parseInt(
        parts.find((p) => p.type === 'day')?.value || '0',
        10
      );
      const hour = parseInt(
        parts.find((p) => p.type === 'hour')?.value || '0',
        10
      );
      const minute = parseInt(
        parts.find((p) => p.type === 'minute')?.value || '0',
        10
      );
      const second = parseInt(
        parts.find((p) => p.type === 'second')?.value || '0',
        10
      );

      const localDate = new Date(year, month, day);
      setSelectedDate(localDate);
      setDisplayMonth(month);
      setDisplayYear(year);
      setHours(hour % 12 || 12);
      setMinutes(minute);
      setSeconds(second);
      setAmpm(hour >= 12 ? 'PM' : 'AM');
      setTimezone(tz);
      setUnixTimestamp(value.timestamp || Math.floor(utcDate.getTime() / 1000));

      requestAnimationFrame(() => {
        isUpdatingFromTimestamp.current = false;
      });
    }
  }, [value]);

  const calculateUTCTimestamp = (
    year: number,
    month: number,
    day: number,
    hour: number,
    minute: number,
    second: number,
    timezone: string
  ): number => {
    // Create formatter for the target timezone
    const formatter = new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
    });

    const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}T${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}:${String(second).padStart(2, '0')}`;
    const baseUTC = new Date(dateStr + 'Z').getTime();

    let low = baseUTC - 15 * 60 * 60 * 1000;
    let high = baseUTC + 15 * 60 * 60 * 1000;
    let bestMatch: number | null = null;
    let bestScore = Infinity;

    for (let iteration = 0; iteration < 100; iteration++) {
      const mid = Math.floor((low + high) / 2);
      const candidateUTC = new Date(mid);

      const parts = formatter.formatToParts(candidateUTC);
      const displayedYear = parseInt(
        parts.find((p) => p.type === 'year')?.value || '0',
        10
      );
      const displayedMonth = parseInt(
        parts.find((p) => p.type === 'month')?.value || '0',
        10
      );
      const displayedDay = parseInt(
        parts.find((p) => p.type === 'day')?.value || '0',
        10
      );
      const displayedHour = parseInt(
        parts.find((p) => p.type === 'hour')?.value || '0',
        10
      );
      const displayedMinute = parseInt(
        parts.find((p) => p.type === 'minute')?.value || '0',
        10
      );
      const displayedSecond = parseInt(
        parts.find((p) => p.type === 'second')?.value || '0',
        10
      );

      if (
        displayedYear === year &&
        displayedMonth === month + 1 &&
        displayedDay === day &&
        displayedHour === hour &&
        displayedMinute === minute &&
        displayedSecond === second
      ) {
        return Math.floor(mid / 1000);
      }

      const yearDiff = Math.abs(displayedYear - year);
      const monthDiff = Math.abs(displayedMonth - (month + 1));
      const dayDiff = Math.abs(displayedDay - day);
      const hourDiff = Math.abs(displayedHour - hour);
      const minuteDiff = Math.abs(displayedMinute - minute);
      const secondDiff = Math.abs(displayedSecond - second);

      const score =
        yearDiff * 1000000 +
        monthDiff * 10000 +
        dayDiff * 100 +
        hourDiff * 10 +
        minuteDiff +
        secondDiff * 0.1;

      if (score < bestScore) {
        bestScore = score;
        bestMatch = mid;
      }

      const targetValue =
        year * 10000000000 +
        (month + 1) * 100000000 +
        day * 1000000 +
        hour * 10000 +
        minute * 100 +
        second;
      const displayedValue =
        displayedYear * 10000000000 +
        displayedMonth * 100000000 +
        displayedDay * 1000000 +
        displayedHour * 10000 +
        displayedMinute * 100 +
        displayedSecond;

      if (displayedValue < targetValue) {
        low = mid + 1;
      } else if (displayedValue > targetValue) {
        high = mid - 1;
      } else {
        break;
      }

      if (high <= low) {
        break;
      }
    }

    if (bestMatch !== null) {
      return Math.floor(bestMatch / 1000);
    }

    return Math.floor(baseUTC / 1000);
  };

  useEffect(() => {
    if (isUpdatingFromTimestamp.current) {
      return;
    }

    const updateTimestamp = () => {
      const year = selectedDate.getFullYear();
      const month = selectedDate.getMonth();
      const day = selectedDate.getDate();
      const hour24 = ampm === 'PM' ? (hours % 12) + 12 : hours % 12;

      const timestamp = calculateUTCTimestamp(
        year,
        month,
        day,
        hour24,
        minutes,
        seconds,
        timezone
      );

      setUnixTimestamp(timestamp);
    };

    updateTimestamp();
  }, [selectedDate, hours, minutes, seconds, ampm, timezone]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        modalRef.current &&
        !modalRef.current.contains(event.target as Node)
      ) {
        // Don't close on outside click, user must use Save and Close button
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const getDaysInMonth = (month: number, year: number) => {
    return new Date(year, month + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (month: number, year: number) => {
    return new Date(year, month, 1).getDay();
  };

  const handleDateSelect = (day: number) => {
    const newDate = new Date(displayYear, displayMonth, day);
    setSelectedDate(newDate);
  };

  const handlePrevMonth = () => {
    if (displayMonth === 0) {
      setDisplayMonth(11);
      setDisplayYear(displayYear - 1);
    } else {
      setDisplayMonth(displayMonth - 1);
    }
  };

  const handleNextMonth = () => {
    if (displayMonth === 11) {
      setDisplayMonth(0);
      setDisplayYear(displayYear + 1);
    } else {
      setDisplayMonth(displayMonth + 1);
    }
  };

  const handleUnixTimestampChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const timestamp = parseInt(e.target.value, 10);
    if (!isNaN(timestamp) && timestamp > 0) {
      isUpdatingFromTimestamp.current = true;
      setUnixTimestamp(timestamp);
      const utcDate = new Date(timestamp * 1000);

      const formatter = new Intl.DateTimeFormat('en-US', {
        timeZone: timezone,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false,
      });

      const parts = formatter.formatToParts(utcDate);
      const year = parseInt(
        parts.find((p) => p.type === 'year')?.value || '0',
        10
      );
      const month =
        parseInt(parts.find((p) => p.type === 'month')?.value || '0', 10) - 1;
      const day = parseInt(
        parts.find((p) => p.type === 'day')?.value || '0',
        10
      );
      const hour = parseInt(
        parts.find((p) => p.type === 'hour')?.value || '0',
        10
      );
      const minute = parseInt(
        parts.find((p) => p.type === 'minute')?.value || '0',
        10
      );
      const second = parseInt(
        parts.find((p) => p.type === 'second')?.value || '0',
        10
      );

      setSelectedDate(new Date(year, month, day));
      setDisplayMonth(month);
      setDisplayYear(year);
      setHours(hour % 12 || 12);
      setMinutes(minute);
      setSeconds(second);
      setAmpm(hour >= 12 ? 'PM' : 'AM');

      // Reset flag after state updates
      setTimeout(() => {
        isUpdatingFromTimestamp.current = false;
      }, 0);
    }
  };

  const handleSave = () => {
    const dateFromTimestamp = new Date(unixTimestamp * 1000);

    const result: DateTimePickerValue = {
      datetime: dateFromTimestamp,
      timestamp: unixTimestamp,
      timezone: timezone,
    };

    onChange(result);
    onClose();
  };

  const monthNames = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ];

  const dayNames = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'];

  const daysInMonth = getDaysInMonth(displayMonth, displayYear);
  const firstDay = getFirstDayOfMonth(displayMonth, displayYear);
  const days = [];

  for (let i = 0; i < firstDay; i++) {
    days.push(null);
  }

  for (let day = 1; day <= daysInMonth; day++) {
    days.push(day);
  }

  const isSelectedDate = (day: number | null) => {
    if (day === null) return false;
    return (
      selectedDate.getDate() === day &&
      selectedDate.getMonth() === displayMonth &&
      selectedDate.getFullYear() === displayYear
    );
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-foreground/50 flex items-center justify-center z-50">
      <div
        ref={modalRef}
        className="bg-white rounded-lg shadow-2xl w-full max-w-3xl mx-4 overflow-hidden"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 bg-gray-50">
          <div></div>
          <h2 className="text-sm font-semibold text-gray-700">
            Select a Date and Time
          </h2>
          <button
            onClick={onClose}
            className="p-1.5 hover:bg-gray-200 rounded transition-colors"
          >
            <X className="w-4 h-4 text-gray-500" />
          </button>
        </div>

        <div className="p-4">
          <div className="flex flex-col lg:flex-row gap-4">
            <div className="flex-1 bg-white rounded-md p-3 border border-gray-200">
              <div className="flex items-center justify-between mb-3">
                <button
                  onClick={handlePrevMonth}
                  className="p-1.5 hover:bg-gray-100 rounded transition-colors"
                >
                  <ChevronLeft className="w-4 h-4 text-gray-600" />
                </button>
                <h3 className="text-sm font-semibold text-gray-800">
                  {monthNames[displayMonth]} {displayYear}
                </h3>
                <button
                  onClick={handleNextMonth}
                  className="p-1.5 hover:bg-gray-100 rounded transition-colors"
                >
                  <ChevronRight className="w-4 h-4 text-gray-600" />
                </button>
              </div>

              <div className="grid grid-cols-7 gap-0.5 mb-1.5">
                {dayNames.map((day) => (
                  <div
                    key={day}
                    className="text-center text-xs font-medium text-gray-500 py-1.5"
                  >
                    {day}
                  </div>
                ))}
              </div>

              <div className="grid grid-cols-7 gap-0.5">
                {days.map((day, index) => (
                  <button
                    key={index}
                    onClick={() => day !== null && handleDateSelect(day)}
                    disabled={day === null}
                    className={`
                      aspect-square flex items-center justify-center text-xs rounded transition-all
                      ${day === null ? 'cursor-default' : 'cursor-pointer hover:bg-primary-background'}
                      ${
                        isSelectedDate(day)
                          ? 'bg-primary text-white font-semibold hover:bg-primary-hover shadow-sm'
                          : 'text-gray-700 hover:bg-gray-50'
                      }
                    `}
                  >
                    {day}
                  </button>
                ))}
              </div>
            </div>

            <div className="flex-1 space-y-4">
              <div className="bg-gradient-to-br from-gray-50 to-white rounded-lg p-4 border border-gray-200 shadow-sm">
                <label className="block text-xs font-semibold text-gray-700 mb-3 uppercase tracking-wide">
                  Time
                </label>
                <div className="flex items-center gap-2 justify-center">
                  <div className="flex items-center gap-1.5 bg-white rounded-md p-1.5 border border-gray-200 shadow-sm">
                    <Input
                      type="number"
                      min="1"
                      max="12"
                      value={hours.toString()}
                      onChange={(e) => {
                        const val = parseInt(e.target.value, 10);
                        if (!isNaN(val) && val >= 1 && val <= 12) {
                          setHours(val);
                        } else if (e.target.value === '') {
                          setHours(1);
                        }
                      }}
                      className="w-12 text-center text-sm font-semibold border-0 focus:ring-0 focus:outline-none"
                      size="sm"
                    />
                    <span className="text-gray-500 text-lg font-medium">:</span>
                    <Input
                      type="number"
                      min="0"
                      max="59"
                      value={minutes.toString().padStart(2, '0')}
                      onChange={(e) => {
                        const val = parseInt(e.target.value, 10);
                        if (!isNaN(val) && val >= 0 && val <= 59) {
                          setMinutes(val);
                        } else if (e.target.value === '') {
                          setMinutes(0);
                        }
                      }}
                      className="w-12 text-center text-sm font-semibold border-0 focus:ring-0 focus:outline-none"
                      size="sm"
                    />
                    <span className="text-gray-500 text-lg font-medium">:</span>
                    <Input
                      type="number"
                      min="0"
                      max="59"
                      value={seconds.toString().padStart(2, '0')}
                      onChange={(e) => {
                        const val = parseInt(e.target.value, 10);
                        if (!isNaN(val) && val >= 0 && val <= 59) {
                          setSeconds(val);
                        } else if (e.target.value === '') {
                          setSeconds(0);
                        }
                      }}
                      className="w-12 text-center text-sm font-semibold border-0 focus:ring-0 focus:outline-none"
                      size="sm"
                    />
                  </div>
                  <Select
                    options={[
                      { value: 'AM', label: 'AM' },
                      { value: 'PM', label: 'PM' },
                    ]}
                    value={ampm}
                    onChange={(e) => setAmpm(e.target.value as 'AM' | 'PM')}
                    className="w-20 border-gray-200 shadow-sm"
                    size="sm"
                  />
                </div>
              </div>

              {/* Timezone Selector */}
              <div className="bg-gradient-to-br from-gray-50 to-white rounded-lg p-4 border border-gray-200 shadow-sm">
                <label className="block text-xs font-semibold text-gray-700 mb-3 uppercase tracking-wide">
                  Timezone
                </label>
                <Select
                  options={getTimezones()}
                  value={timezone}
                  onChange={(e) => setTimezone(e.target.value)}
                  placeholder="Select Timezone"
                  className="w-full border-gray-200 shadow-sm"
                  size="sm"
                />
              </div>

              {/* Separator */}
              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gray-200"></div>
                </div>
                <div className="relative flex justify-center text-xs">
                  <span className="bg-white px-3 text-gray-400 font-medium">
                    or
                  </span>
                </div>
              </div>

              {/* Unix Timestamp Input */}
              <div className="bg-gradient-to-br from-gray-50 to-white rounded-lg p-4 border border-gray-200 shadow-sm">
                <label className="block text-xs font-semibold text-gray-700 mb-3 uppercase tracking-wide">
                  Unix Timestamp (UTC)
                </label>
                <Input
                  type="number"
                  value={unixTimestamp.toString()}
                  onChange={handleUnixTimestampChange}
                  placeholder="Enter Unix timestamp"
                  className="w-full text-sm font-mono border-gray-200 shadow-sm focus:border-primary focus:ring-primary"
                  size="sm"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-2 px-4 py-3 border-t border-gray-200 bg-gray-50">
          <button
            onClick={onClose}
            className="px-3 py-1.5 text-gray-700 bg-white hover:bg-gray-100 border border-gray-300 rounded-md transition-colors text-sm font-medium"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            className="px-3 py-1.5 text-white bg-primary hover:bg-primary-hover rounded-md transition-colors text-sm font-medium shadow-sm"
          >
            Save and Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default DateTimePicker;
